import React, { useCallback, useEffect, useMemo, useState } from 'react';
import http, { resolveApiUrl } from '../../api/http';
import './mobile.css';

const resolveCheckinPhotoUrl = (rawUrl) => {
  const input = String(rawUrl || '').trim();
  if (!input) return '';

  const absolute = resolveApiUrl(input);
  const token = String(localStorage.getItem('token') || '').trim();
  if (!token) return absolute;

  try {
    const url = new URL(absolute, window.location.origin);
    if (!url.searchParams.get('token') && !url.searchParams.get('access_token')) {
      url.searchParams.set('token', token);
    }
    return url.toString();
  } catch (e) {
    const joiner = absolute.includes('?') ? '&' : '?';
    return `${absolute}${joiner}token=${encodeURIComponent(token)}`;
  }
};

const categoryOptions = [
  { value: 'arrive_work', label: '到岗' },
  { value: 'morning', label: '早班' },
  { value: 'mid', label: '中班' },
  { value: 'night', label: '晚班' },
  { value: 'arrive_home', label: '到家' },
];

export default function MobileCheckinPage() {
  // Attendance (behavior daily checkin)
  const [attCategory, setAttCategory] = useState('arrive_work');
  const [attFile, setAttFile] = useState(null);
  const [attSaving, setAttSaving] = useState(false);
  const [attError, setAttError] = useState('');
  const [attList, setAttList] = useState([]);

  // Safety inspection
  const [siSaving, setSiSaving] = useState(false);
  const [siError, setSiError] = useState('');
  const [siSuccess, setSiSuccess] = useState('');
  const [siList, setSiList] = useState([]);
  const [siForm, setSiForm] = useState({
    title: '',
    location: '',
    result: 'PASS',
    issues: '',
    suggestions: '',
    rectification_responsible: '',
    rectification_deadline: '',
    remark: '',
  });
  const [siPhoto, setSiPhoto] = useState(null);

  const loadAttList = useCallback(async () => {
    try {
      const res = await http.get('/behavior/daily-checkin/my', { params: { page: 1, pageSize: 20 } });
      setAttList(Array.isArray(res.data?.list) ? res.data.list : []);
      setAttError('');
    } catch (e) {
      setAttList([]);
      setAttError(e?.response?.data?.error || e?.response?.data?.message || e?.message || '加载打卡历史失败');
    }
  }, []);

  const loadSiList = useCallback(async () => {
    try {
      const res = await http.get('/safety-inspections/records', { params: { mine: true, page: 1, page_size: 10 } });
      setSiList(Array.isArray(res.data?.data) ? res.data.data : []);
      setSiError('');
    } catch (e) {
      setSiList([]);
      setSiError(e?.response?.data?.error || e?.response?.data?.message || e?.message || '加载巡检记录失败');
    }
  }, []);

  useEffect(() => {
    loadAttList();
    loadSiList();
  }, [loadAttList, loadSiList]);

  const canAttSubmit = useMemo(() => !!attFile && !!attCategory, [attFile, attCategory]);

  const submitAtt = async (e) => {
    e?.preventDefault?.();
    if (!canAttSubmit) return;
    setAttSaving(true);
    setAttError('');
    try {
      const fd = new FormData();
      fd.append('category', attCategory);
      fd.append('file', attFile);
      await http.post('/behavior/daily-checkin/checkins', fd);
      setAttFile(null);
      await loadAttList();
    } catch (err) {
      setAttError(err?.response?.data?.error || err?.response?.data?.message || err?.message || '打卡失败');
    } finally {
      setAttSaving(false);
    }
  };

  const onSiChange = (k) => (e) => setSiForm((p) => ({ ...p, [k]: e.target.value }));

  const canSiSubmit = useMemo(() => {
    const r = String(siForm.result || '').toUpperCase();
    if (!['PASS', 'FAIL'].includes(r)) return false;
    return true;
  }, [siForm.result]);

  const submitSi = async (e) => {
    e?.preventDefault?.();
    if (!canSiSubmit) return;

    setSiSaving(true);
    setSiError('');
    setSiSuccess('');

    try {
      const payload = {
        title: siForm.title,
        location: siForm.location,
        result: String(siForm.result || '').toUpperCase(),
        issues: siForm.issues,
        suggestions: siForm.suggestions,
        rectification_responsible: siForm.rectification_responsible,
        rectification_deadline: siForm.rectification_deadline,
        remark: siForm.remark,
        status: 'OPEN',
      };

      const res = await http.post('/safety-inspections/records', payload);
      const id = res?.data?.id;

      if (id && siPhoto) {
        const fd = new FormData();
        fd.append('file', siPhoto);
        await http.post(`/safety-inspections/records/${id}/attachments`, fd);
      }

      setSiSuccess(id ? `巡检打卡已提交（ID: ${id}）` : '巡检打卡已提交');
      setSiForm({
        title: '',
        location: '',
        result: 'PASS',
        issues: '',
        suggestions: '',
        rectification_responsible: '',
        rectification_deadline: '',
        remark: '',
      });
      setSiPhoto(null);
      await loadSiList();
    } catch (err) {
      setSiError(err?.response?.data?.error || err?.response?.data?.message || err?.message || '巡检打卡失败');
    } finally {
      setSiSaving(false);
    }
  };

  return (
    <div className="m-section">
      <div className="m-card m-cardPad">
        <div className="m-h2">签到打卡（考勤）</div>
        <div className="m-muted">自拍打卡并记录历史（需要对应权限）。</div>
        {attError ? <div className="m-alert m-alertErr" style={{ marginTop: 10 }}>{attError}</div> : null}
      </div>

      <form className="m-card m-cardPad m-form" onSubmit={submitAtt}>
        <div className="m-row">
          <div className="m-field">
            <div className="m-label">打卡类型</div>
            <select className="m-select" value={attCategory} onChange={(e) => setAttCategory(e.target.value)}>
              {categoryOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="m-field">
            <div className="m-label">自拍照片</div>
            <input
              className="m-input"
              type="file"
              accept="image/*"
              capture="user"
              onChange={(e) => setAttFile((e.target.files && e.target.files[0]) ? e.target.files[0] : null)}
            />
          </div>
        </div>

        <button className="btn" type="submit" disabled={!canAttSubmit || attSaving}>
          {attSaving ? '打卡中...' : '提交考勤打卡'}
        </button>

        <button type="button" className="btn btn-secondary" onClick={loadAttList}>
          刷新打卡历史
        </button>

        {attList.length ? (
          <div className="m-list">
            {attList.slice(0, 10).map((it) => (
              <div key={it.id} className="m-item">
                <div className="m-itemTitle">{it.category || '—'}</div>
                <div className="m-itemMeta">{it.created_at ? String(it.created_at).replace('T', ' ').slice(0, 19) : '—'}</div>
                {it.photo_url ? (
                  <a href={resolveCheckinPhotoUrl(it.photo_url)} target="_blank" rel="noreferrer" className="m-muted">查看照片</a>
                ) : null}
              </div>
            ))}
          </div>
        ) : (
          <div className="m-alert">暂无考勤打卡记录</div>
        )}
      </form>

      <div className="m-divider" />

      <div className="m-card m-cardPad">
        <div className="m-h2">安全巡检打卡</div>
        <div className="m-muted">提交一条巡检记录，可附带现场照片（需要 hazard_view/hazard_manage 权限）。</div>
        {siError ? <div className="m-alert m-alertErr" style={{ marginTop: 10 }}>{siError}</div> : null}
        {siSuccess ? <div className="m-alert" style={{ marginTop: 10 }}>{siSuccess}</div> : null}
      </div>

      <form className="m-card m-cardPad m-form" onSubmit={submitSi}>
        <div className="m-row">
          <div className="m-field">
            <div className="m-label">结果</div>
            <select className="m-select" value={siForm.result} onChange={onSiChange('result')}>
              <option value="PASS">PASS（合格）</option>
              <option value="FAIL">FAIL（不合格）</option>
            </select>
          </div>
          <div className="m-field">
            <div className="m-label">照片（可选）</div>
            <input
              className="m-input"
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => setSiPhoto((e.target.files && e.target.files[0]) ? e.target.files[0] : null)}
            />
          </div>
        </div>

        <div className="m-row">
          <div className="m-field">
            <div className="m-label">标题（可选）</div>
            <input className="m-input" value={siForm.title} onChange={onSiChange('title')} placeholder="例如：车间巡检" />
          </div>
          <div className="m-field">
            <div className="m-label">地点（可选）</div>
            <input className="m-input" value={siForm.location} onChange={onSiChange('location')} placeholder="例如：1号车间" />
          </div>
        </div>

        <div className="m-field">
          <div className="m-label">问题描述（可选）</div>
          <textarea className="m-textarea" value={siForm.issues} onChange={onSiChange('issues')} />
        </div>

        <div className="m-field">
          <div className="m-label">建议措施（可选）</div>
          <textarea className="m-textarea" value={siForm.suggestions} onChange={onSiChange('suggestions')} />
        </div>

        <div className="m-row">
          <div className="m-field">
            <div className="m-label">整改责任人（可选）</div>
            <input className="m-input" value={siForm.rectification_responsible} onChange={onSiChange('rectification_responsible')} />
          </div>
          <div className="m-field">
            <div className="m-label">整改期限（可选）</div>
            <input className="m-input" type="date" value={siForm.rectification_deadline} onChange={onSiChange('rectification_deadline')} />
          </div>
        </div>

        <div className="m-field">
          <div className="m-label">备注（可选）</div>
          <input className="m-input" value={siForm.remark} onChange={onSiChange('remark')} />
        </div>

        <button className="btn" type="submit" disabled={!canSiSubmit || siSaving}>
          {siSaving ? '提交中...' : '提交巡检打卡'}
        </button>

        <button type="button" className="btn btn-secondary" onClick={loadSiList}>
          刷新巡检记录
        </button>

        {siList.length ? (
          <div className="m-list">
            {siList.slice(0, 10).map((r) => (
              <div key={r.id} className="m-item">
                <div className="m-itemTitle">{r.title || '巡检记录'} <span className="badge">{r.result}</span></div>
                <div className="m-itemMeta">{r.inspection_date ? String(r.inspection_date).replace('T', ' ').slice(0, 19) : '—'}{r.location ? `｜${r.location}` : ''}</div>
                {Array.isArray(r.attachments) && r.attachments.length ? (
                  <a className="m-muted" href={r.attachments[0]?.url || r.attachments[0]?.file_path} target="_blank" rel="noreferrer">查看附件</a>
                ) : null}
              </div>
            ))}
          </div>
        ) : (
          <div className="m-alert">暂无巡检记录</div>
        )}
      </form>
    </div>
  );
}
